<template>
  <div class="notice-modal" :style="modalPosition">
    <div class="notice-header">
      <span class="notice-title">
        <i class="bi bi-volume-up fw-bolder" style="font-size:1.3rem;"></i>&nbsp;&nbsp;엔타비에서 알려드립니다
      </span>
      <!-- 상단 닫기(X) 버튼 : iOS 하단 툴바 영역과 무관하게 항상 눌림 -->
      <button type="button" class="close-btn" aria-label="닫기" @click="$emit('close')">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="notice-body">
      <div class="notice-content" ref="scrollBox" @scroll.passive="checkScroll">
        <div class="markdown-body ql-editor" ref="contentInner" v-html="renderedHtml"></div>
      </div>

      <!-- 아래에 내용이 더 있을 때만 표시 (끝까지 내리면 사라짐) -->
      <transition name="hint-fade">
        <div class="scroll-hint" v-if="hasMore">
          <button type="button" class="scroll-hint-btn" @click="scrollDown">
            아래 내용 더보기 <i class="bi bi-chevron-double-down"></i>
          </button>
        </div>
      </transition>
    </div>

    <div class="notice-actions">
      <button type="button" class="btn-primary" @click="$emit('close')">확인</button>
      <button type="button" class="btn-secondary" @click="doNotShowToday">오늘 그만보기</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  data() {
    return {
      renderedHtml: "",
      hasMore: false,       // 아래에 더 읽을 내용이 있는지
      resizeObs: null       // 이미지 로딩 등으로 높이가 바뀔 때 재계산용
    };
  },
  props: {
    content: { type: String, required: true },
    idx: { type: Number, required: true }
  },
  computed: {
    modalPosition() {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;

      // 모바일: 가로로 나열하면 2번째 팝업부터 화면 밖으로 나가므로 같은 자리에 겹쳐 쌓는다
      //         (위치/크기는 CSS가 담당, 여기서는 z-index와 살짝 어긋나는 오프셋만 준다)
      if (isMobile) {
        return {
          position: "fixed",
          zIndex: 9999 + this.idx,
          "--stack-offset": `${this.idx * 10}px`
        };
      }

      // PC: 기존처럼 왼쪽부터 가로로 나열
      const startX = 10;
      const startY = 20;
      const modalWidth = 400;
      const gap = 20, offsetX = modalWidth + gap;
      return {
        left: `${startX + this.idx * offsetX}px`,
        top: `${startY}px`,
        position: "fixed",
        zIndex: 9999 + this.idx
      };
    }
  },
  created() { this.getNoticeContent(); },

  mounted() {
    // 공지 안의 이미지가 늦게 로딩되면 높이가 바뀌므로 크기 변화를 감시
    if (window.ResizeObserver) {
      this.resizeObs = new ResizeObserver(() => this.checkScroll());
      if (this.$refs.contentInner) this.resizeObs.observe(this.$refs.contentInner);
      if (this.$refs.scrollBox) this.resizeObs.observe(this.$refs.scrollBox);
    }
  },

  beforeUnmount() {
    if (this.resizeObs) this.resizeObs.disconnect();
  },
  methods: {
    async getNoticeContent() {
      let filename = `${this.content}`, ctCd = filename.substring(0, 2);
      try {
        let response = await axios.get(`/uploads/notice/${ctCd}/${filename}`);
        if (response.data.success) {
          this.renderedHtml = response.data.content;
          await this.$nextTick();
          this.checkScroll();
        } else {
          throw new Error("Failed to load document");
        }
      } catch (error) {
        this.$swal.fire("", "공지상세내용이 없습니다", "warning");
      }
    },

    // 남은 스크롤 거리가 있으면 힌트 표시
    checkScroll() {
      const el = this.$refs.scrollBox;
      if (!el) return;
      this.hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 12;
    },

    // 힌트 버튼 클릭 시 화면의 70%만큼 부드럽게 내림
    scrollDown() {
      const el = this.$refs.scrollBox;
      if (!el) return;
      el.scrollBy({ top: el.clientHeight * 0.7, behavior: 'smooth' });
    },

    doNotShowToday() {
      const nextDay = new Date();
      nextDay.setHours(23, 59, 59, 999);     // 당일 23:59:59까지
      localStorage.setItem(
        `hideNotice_${this.content}`,
        nextDay.getTime().toString()
      );
      this.$emit('close');  // 팝업 닫기
    }
  }
}
</script>

<style scoped>
.notice-modal {
  width: 400px;
  height: auto;
  max-width: 95vw;
  max-height: calc(100vh - 40px);
  background: rgba(255,255,255,0.80);
  -webkit-backdrop-filter: blur(18px);   /* 구형 iOS Safari 대응 */
  backdrop-filter: blur(18px);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 6px 36px rgba(30,50,110,0.16), 0 1.5px 4px rgba(0,0,0,0.05);
  padding: 0;
  display: flex;
  flex-direction: column;
  animation: popupAppear 0.29s cubic-bezier(.62,1.41,.69,1) both;
}

@keyframes popupAppear {
  0% { transform: scale(0.93) translateY(25px); opacity: 0;}
  100% { transform: scale(1) translateY(0); opacity: 1;}
}

.notice-header {
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between;
  height: 50px;
  padding: 0 0.6rem 0 0 !important;
  background-color: #86cd53d6;
}

.notice-title {
  font-weight: 600;
  font-size: 1.15rem;
  color: white;
  letter-spacing: 0.01em;
  padding-left: 1rem;
  display: flex; align-items: center;
}

.notice-content,
.notice-actions {
  padding: 0;
  margin: 0;
}

/* 상단 X 버튼 */
.close-btn {
  flex-shrink: 0;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: none; border: none;
  font-size: 1.25rem;
  color: #fff; opacity: 0.9; cursor: pointer;
  border-radius: 50%;
  transition: background 0.15s;
  -webkit-tap-highlight-color: transparent;
}
.close-btn:hover { background: rgba(255,255,255,0.2); opacity: 1; }

.notice-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;          /* 팝업 스크롤이 뒤 페이지로 번지지 않게 */
  margin: 0; padding: 1.15rem 1.6rem;
  font-size: 1rem;
  line-height: 1.65;
  word-break: break-word;
}

/* 본문 + 스크롤힌트 래퍼 */
.notice-body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 하단 페이드 + 더보기 버튼 */
.scroll-hint {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 80px;
  display: flex; align-items: flex-end; justify-content: center;
  padding-bottom: 10px;
  background: linear-gradient(to bottom,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.92) 65%,
    #fff 100%);
  pointer-events: none;          /* 그라데이션 영역은 터치 통과 → 스크롤 방해 안 함 */
}

.scroll-hint-btn {
  pointer-events: auto;          /* 버튼만 클릭 가능 */
  border: none;
  border-radius: 20px;
  background: #86cd53;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0.35rem 0.95rem;
  box-shadow: 0 2px 8px rgba(26,46,10,0.2);
  display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.scroll-hint-btn i {
  animation: hintBounce 1.4s ease-in-out infinite;
}

@keyframes hintBounce {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(3px); }
}

.hint-fade-enter-active,
.hint-fade-leave-active { transition: opacity 0.25s ease; }
.hint-fade-enter-from,
.hint-fade-leave-to { opacity: 0; }

.notice-actions {
  flex-shrink: 0;
  padding: 1rem 1.6rem 1.3rem 0;
  display: flex; gap: 0.5rem; justify-content: flex-end;
  background: transparent;
}

.btn-primary {
  background: linear-gradient(92deg, #2e6cff 70%, #3cd1ef 102%);
  border: none; color: #fff; font-weight: 600;
  border-radius: 7px; padding: 0.55rem 2.1rem;
  box-shadow: 0 2px 10px rgba(20,70,255,0.09);
  cursor: pointer; transition: background 0.2s;
  font-size: 0.9rem;
}
.btn-primary:hover { background: linear-gradient(92deg, #1957e7 72%, #32b7cb 102%); }

/* 오늘 그만보기 버튼 */
.btn-secondary {
  background: linear-gradient(92deg, #3f4248 70%, #348ea0 102%);
  border: none; color: #fff; font-weight: 600;
  border-radius: 7px; padding: 0.55rem 2.1rem;
  box-shadow: 0 2px 10px rgba(20,70,255,0.09);
  cursor: pointer; transition: background 0.2s;
  font-size: 0.9rem;
}
.btn-secondary:hover { background: linear-gradient(92deg, #3c404a 72%, #1e3336 102%);}

/* ============================================================
   모바일 (iOS Safari 하단 툴바 / 홈 인디케이터 대응)
   - 90vh 고정높이 제거 → 내용만큼 높이, 최대치는 svh 기준
   - svh = 툴바가 "보일 때"의 화면 높이 → 버튼이 툴바 뒤로 들어가지 않음
   ============================================================ */
@media (max-width: 768px) {
  .notice-modal {
    left: 2vw;
    right: 2vw;
    width: auto;
    max-width: none;
    top: calc(env(safe-area-inset-top, 0px) + 12px + var(--stack-offset, 0px));
    height: auto;
    max-height: 75vh;                                     /* svh 미지원 구형 iOS fallback */
    max-height: calc(100svh - 60px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
    border-radius: 12px;
  }
  .notice-content { padding-left: 1.1rem; padding-right: 1.1rem; }
  .notice-actions {
    padding-left: 1.1rem;
    padding-right: 1.1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }
  .notice-header { height: 56px; }
  .notice-title { padding-left: 1rem; }
  .btn-primary, .btn-secondary { padding: 0.65rem 1.4rem; }   /* 터치영역 확보 */
}
</style>